import type { ProductVertical } from "./operational-permissions";
import type { OperationalRoleTemplate } from "./operational-role-templates";
import { roleTemplatesForProfile } from "./operational-role-templates";

export interface BusinessOperationalPreset {
  profile: string;
  vertical: ProductVertical;
  label: string;
  defaultRoles: string[];
  defaultNavigation: string[];
  defaultDashboards: string[];
  recommendedFeatures: string[];
  operationalModules: string[];
  onboardingFlow: string[];
  terminology: Record<string, string>;
}

export const FM_BUSINESS_OPERATIONAL_PRESETS: BusinessOperationalPreset[] = [
  preset("fast_food", "fm", "Fast food", ["owner", "cashier", "kitchen", "delivery"], ["ordenes", "operaciones", "catalogacion"], ["orders_today", "prep_queue"], ["quick_add", "payment_mercadopago"], ["ordenes", "pesaje", "pagos"], ["Cargá el menú", "Activá retiro/delivery", "Probá un pedido"]),
  preset("restaurant", "fm", "Restaurante", ["owner", "branch_manager", "kitchen", "cashier"], ["ordenes", "operaciones", "catalogacion"], ["orders_today", "delayed_orders"], ["variants_addons", "loyalty_points"], ["ordenes", "operaciones", "produccion"], ["Definí mesas/retiro", "Configurá tiempos de preparación"]),
  preset("market", "fm", "Almacén / Mercado", ["owner", "cashier", "branch_manager"], ["ordenes", "catalogacion", "inventario"], ["orders_today", "low_stock"], ["pesaje_module", "stock_management"], ["ordenes", "pesaje", "inventario"], ["Productos por peso", "Activá pesaje"]),
  preset("pharmacy", "fm", "Farmacia", ["owner", "pharmacy_counter", "delivery"], ["ordenes", "clientes", "catalogacion"], ["orders_queue"], ["customer_accounts", "gift_cards"], ["ordenes", "clientes", "inventario"], ["Control de recetas (notas)", "Entregas"]),
  preset("fashion", "fm", "Indumentaria", ["owner", "cashier", "branch_manager"], ["catalogacion", "ordenes", "clientes"], ["orders_today"], ["variants_addons", "filters"], ["catalogacion", "ordenes"], ["Variantes por talle/color"]),
  preset("bazar", "fm", "Bazar", ["owner", "cashier"], ["catalogacion", "ordenes", "inventario"], ["orders_today"], ["combos", "variants_addons"], ["catalogacion", "inventario"], ["Organizá por categorías"]),
  preset("catalog", "fm", "Catálogo", ["owner", "cashier"], ["catalogacion", "ordenes"], ["orders_today"], ["storefront"], ["catalogacion", "ordenes"], ["Publicá la tienda"]),
  preset("dietetics", "fm", "Dietética", ["owner", "cashier"], ["catalogacion", "inventario", "clientes"], ["orders_today"], ["brands_nav", "customer_accounts"], ["catalogacion", "clientes"], ["Marcas y categorías"]),
  preset("digital_products", "fm", "Productos digitales", ["owner", "cashier"], ["catalogacion", "digital", "clientes"], ["orders_today", "digital_downloads"], ["digital_products", "customer_accounts", "payment_mercadopago"], ["catalogacion", "digital", "clientes"], ["Subí tu primer producto digital", "Configurá entrega automática", "Probá checkout y descarga"]),
  preset("pet", "fm", "Pet shop", ["owner", "cashier", "branch_manager"], ["catalogacion", "ordenes", "inventario"], ["orders_today", "low_stock"], ["variants_addons", "customer_accounts"], ["catalogacion", "ordenes", "inventario"], ["Cargá tu catálogo", "Activá stock por producto"]),
  preset("cafe", "fm", "Cafetería / Panadería", ["owner", "cashier", "kitchen"], ["ordenes", "operaciones", "catalogacion"], ["orders_today", "prep_queue"], ["combos", "pesaje_module"], ["ordenes", "pesaje", "operaciones"], ["Cargá tu carta", "Activá retiro en mostrador"]),
];

export const BH_BUSINESS_OPERATIONAL_PRESETS: BusinessOperationalPreset[] = [
  preset("beauty_center", "bh", "Centro de belleza", ["owner", "receptionist", "professional"], ["agenda", "bookings", "clients"], ["today_bookings"], ["online_booking", "reminders"], ["agenda", "clients", "services"], ["Cargá servicios y duración", "Invitá profesionales"]),
  preset("clinic", "bh", "Clínica", ["owner", "receptionist", "professional", "clinic_coordinator"], ["agenda", "clients"], ["today_bookings", "waitlist"], ["patient_records", "online_booking"], ["agenda", "clients", "professionals"], ["Definí especialidades"]),
  preset("health_center", "bh", "Centro de salud", ["owner", "clinic_coordinator", "receptionist"], ["agenda", "clients"], ["today_bookings"], ["multi_professional"], ["agenda", "clients"], ["Horarios por profesional"]),
  preset("consultant", "bh", "Consultoría", ["owner", "professional"], ["agenda", "clientes"], ["my_schedule"], ["calendar_sync"], ["agenda"], ["Bloques de disponibilidad"]),
  preset("fitness", "bh", "Fitness", ["owner", "receptionist", "professional"], ["agenda", "clientes"], ["today_bookings"], ["class_packs"], ["agenda", "clientes"], ["Clases y cupos"]),
  preset("wellness", "bh", "Wellness", ["owner", "receptionist", "professional"], ["agenda", "clientes"], ["today_bookings"], ["reminders"], ["agenda"], ["Servicios y paquetes"]),
  preset("therapy", "bh", "Terapia", ["owner", "professional", "receptionist"], ["agenda", "clientes"], ["my_schedule"], ["online_booking"], ["agenda"], ["Sesiones recurrentes"]),
  preset("coworking", "bh", "Coworking", ["owner", "receptionist"], ["agenda", "reservas"], ["today_bookings"], ["resource_booking"], ["agenda", "salas"], ["Recursos y salas"]),
  preset("education", "bh", "Educación", ["owner", "receptionist", "professional"], ["agenda", "clientes"], ["today_bookings"], ["class_scheduling"], ["agenda", "clientes"], ["Cursos y cupos"]),
];

function preset(
  profile: string,
  vertical: ProductVertical,
  label: string,
  defaultRoles: string[],
  defaultNavigation: string[],
  defaultDashboards: string[],
  recommendedFeatures: string[],
  operationalModules: string[],
  onboardingFlow: string[],
): BusinessOperationalPreset {
  const roles = roleTemplatesForProfile(vertical, profile);
  return {
    profile,
    vertical,
    label,
    defaultRoles,
    defaultNavigation,
    defaultDashboards,
    recommendedFeatures,
    operationalModules,
    onboardingFlow,
    terminology: terminologyFor(vertical, profile, roles),
  };
}

function terminologyFor(
  vertical: ProductVertical,
  profile: string,
  roles: OperationalRoleTemplate[],
): Record<string, string> {
  const base =
    vertical === "fm"
      ? { order: "Pedido", customer: "Cliente", catalog: "Catálogo", branch: "Sucursal" }
      : { order: "Reserva", customer: "Cliente", catalog: "Servicios", branch: "Sede" };
  if (profile === "pharmacy") return { ...base, order: "Pedido", customer: "Paciente" };
  if (profile === "clinic" || profile === "health_center") return { ...base, customer: "Paciente" };
  if (roles.some((r) => r.roleId === "kitchen")) return { ...base, order: "Comanda" };
  return base;
}

export function operationalPreset(
  vertical: ProductVertical,
  profile: string,
): BusinessOperationalPreset | undefined {
  const list = vertical === "fm" ? FM_BUSINESS_OPERATIONAL_PRESETS : BH_BUSINESS_OPERATIONAL_PRESETS;
  return list.find((p) => p.profile === profile);
}
